import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { createPopper, } from '@popperjs/core';
import { Transition } from 'react-transition-group';
import { tabbable } from 'tabbable';
import { InputContext, } from '@core-front/input-provider';
import PropsChecker from '@core-front/props-checker';
import { TooltipColors, } from './tooltip';
const TooltipCore = (props) => {
    const { input } = React.useContext(InputContext);
    return (React.createElement(PropsChecker, { componentProps: props, componentName: "Tooltip", defaultValueName: "defaultIsShown", valueName: "isShown", onChangeNames: "onToggle", onChangeAltNames: ['onShow', 'onHide'] },
        React.createElement(TooltipInner, Object.assign({}, props, { inputIntent: input }))));
};
export default TooltipCore;
export class TooltipInner extends React.PureComponent {
    constructor(props) {
        super(props);
        this.tooltip = null;
        this.referenceElement = null;
        this.popper = null;
        this.focusTimeout = 0;
        this.destroyPopper = () => {
            if (this.popper) {
                this.popper.destroy();
                this.popper = null;
            }
        };
        this.hideToolTipReferenceElement = () => {
            if (!this.tooltip) {
                this.hideTooltip();
                return;
            }
            if (this.geInputIntent(this.props.inputIntent) && !this.tooltip.matches(':hover')) {
                this.hideTooltip();
            }
        };
        this.hideToolTipElement = () => {
            if (!this.referenceElement) {
                this.hideTooltip();
                return;
            }
            if (this.geInputIntent(this.props.inputIntent) && !this.referenceElement.matches(':hover')) {
                this.hideTooltip();
            }
        };
        this.showTooltip = () => {
            if (!this.isShown) {
                if (!this.state.isShown && typeof this.props.isShown === 'undefined') {
                    this.setState({
                        isShown: true,
                    });
                }
                if (this.props.onShow) {
                    this.props.onShow();
                }
                if (this.props.onToggle) {
                    this.props.onToggle(true);
                }
            }
        };
        this.hideTooltip = () => {
            if (this.isShown) {
                if (this.state.isShown && typeof this.props.isShown === 'undefined') {
                    this.setState({
                        isShown: false,
                    });
                }
                if (this.props.onHide) {
                    this.props.onHide();
                }
                if (this.props.onToggle) {
                    this.props.onToggle(false);
                }
            }
        };
        this.toggleTooltip = () => {
            if (this.isShown) {
                this.hideTooltip();
            }
            else {
                this.showTooltip();
            }
        };
        this.onClickOutside = (event) => {
            if (event.target instanceof Element) {
                this.hideIfOutside(event.target);
            }
        };
        this.handleCloseClick = (event) => {
            event.preventDefault();
            this.hideTooltip();
        };
        this.setRef = (element) => {
            this.tooltip = element;
            if (this.props.elementRef) {
                this.props.elementRef(element);
            }
        };
        this.onEntered = () => {
            this.focusFirstElement();
        };
        this.onExited = () => {
            var _a;
            if (this.props.inputIntent !== 'mouse') {
                (_a = this.referenceElement) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
        this.onKeyDown = (event) => {
            switch (event.key) {
                // Invoke this.props.onClose when user hits ESC key.
                case 'Escape':
                case 'Esc':
                    this.hideTooltip();
                    break;
                case 'Tab':
                    /* istanbul ignore else */
                    if (this.tooltip) {
                        const tabbableElements = tabbable(this.tooltip);
                        if (tabbableElements.length) {
                            const firstTabbableElement = tabbableElements[0];
                            const lastTabbableElement = tabbableElements[tabbableElements.length - 1];
                            if (event.shiftKey) {
                                if (document.activeElement === firstTabbableElement) {
                                    event.preventDefault();
                                    lastTabbableElement.focus();
                                }
                            }
                            else {
                                if (document.activeElement === lastTabbableElement) {
                                    event.preventDefault();
                                    firstTabbableElement.focus();
                                }
                            }
                        }
                        else {
                            event.preventDefault();
                        }
                    }
            }
        };
        this.state = {
            isMounted: false,
            isReady: false,
            isShown: props.defaultIsShown || false,
        };
    }
    componentDidMount() {
        this.setReferenceElement();
        this.setState({ isMounted: true }, () => {
            if (this.isShown) {
                this.initPopper();
                this.focusFirstElement();
            }
            if (this.props.inputIntent !== 'initial') {
                this.bindEventListeners(this.geInputIntent(this.props.inputIntent));
            }
        });
    }
    componentDidUpdate(prevProps, prevState) {
        if ((this.geInputIntent(prevProps.inputIntent) !== this.geInputIntent(this.props.inputIntent)) && prevProps.inputIntent !== 'initial' && this.isShown) {
            this.hideTooltip();
        }
        if (this.geInputIntent(prevProps.inputIntent) !== this.geInputIntent(this.props.inputIntent)) {
            this.bindEventListeners(this.geInputIntent(this.props.inputIntent));
        }
        if ((this.state.isShown !== prevState.isShown) || (this.props.isShown !== prevProps.isShown)) {
            if (this.isShown) {
                this.initPopper();
                if (this.tooltip && this.geInputIntent(this.props.inputIntent) === 'mouse' && this.props.trigger !== 'none') {
                    this.tooltip.addEventListener('mouseenter', this.showTooltip);
                    this.tooltip.addEventListener('mouseleave', this.hideToolTipElement);
                }
            }
            else if (!this.isShown) {
                this.destroyPopper();
            }
        }
    }
    componentWillUnmount() {
        this.unbindAllEventListeners();
        this.destroyPopper();
        window.clearTimeout(this.focusTimeout);
    }
    get isShown() {
        return (this.props.onToggle || (this.props.onHide && this.props.onShow)) && typeof this.props.isShown !== 'undefined' ? this.props.isShown : this.state.isShown;
    }
    isSpecColor(color) {
        return color ? color in TooltipColors : false;
    }
    geInputIntent(input) {
        if (this.props.trigger === 'click') {
            return 'touch';
        }
        return input;
    }
    setReferenceElement() {
        if (typeof this.props.referenceElement === 'string') {
            this.referenceElement = document.querySelector(this.props.referenceElement);
        }
        else if (this.props.referenceElement instanceof SVGElement ||
            this.props.referenceElement instanceof HTMLElement) {
            this.referenceElement = this.props.referenceElement;
        }
        else {
            this.referenceElement = this.tooltip && this.tooltip.parentElement;
        }
    }
    focusFirstElement() {
        this.focusTimeout = window.setTimeout(() => {
            /* istanbul ignore else */
            if (this.tooltip && this.props.inputIntent !== 'mouse') {
                const firstTabbableElement = tabbable(this.tooltip)[0];
                if (firstTabbableElement) {
                    firstTabbableElement.focus();
                    return;
                }
                this.tooltip.focus();
            }
        });
    }
    /**
     * Initialize popper.js to automagically position the tooltip according to available space.
     */
    initPopper() {
        if (!this.tooltip || !this.referenceElement) {
            return;
        }
        this.popper = createPopper(this.referenceElement, this.tooltip, {
            modifiers: [
                { name: 'applyStyles', enabled: false },
                {
                    name: 'updateState',
                    enabled: true,
                    phase: 'write',
                    requires: ['computeStyles'],
                    fn: ({ state }) => this.updatePopperState(state),
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['top', 'bottom', 'right', 'left'],
                        padding: { top: 137, right: 17, bottom: 17, left: 17 },
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        padding: 15,
                    },
                },
            ],
            onFirstUpdate: () => {
                if (this.popper) {
                    this.popper.update().then(() => {
                        this.setState({
                            isReady: true,
                        });
                    });
                }
            },
            placement: this.props.placement,
        });
    }
    updatePopperState(state) {
        const popperStyle = Object.assign({}, state.styles.popper);
        const popperAttributes = Object.assign({}, state.attributes.popper);
        const popperArrowStyle = Object.assign({}, state.styles.arrow);
        if (!this.isSpecColor(this.props.color)) {
            popperArrowStyle.color = this.props.color;
        }
        this.setState({
            popperStyle,
            popperAttributes,
            popperArrowStyle,
        });
    }
    bindEventListeners(type) {
        if (this.props.trigger === 'none') {
            return;
        }
        this.unbindAllEventListeners();
        if (this.props.hideWithIntent && this.props.hideWithIntent.includes(this.geInputIntent(this.props.inputIntent))) {
            return;
        }
        switch (type) {
            case 'mouse':
                return this.bindHoverEventListeners();
            case 'keyboard':
            default:
                return this.bindClickEventListeners();
        }
    }
    bindClickEventListeners() {
        if (this.referenceElement) {
            this.referenceElement.addEventListener('click', this.toggleTooltip);
        }
        document.addEventListener('click', this.onClickOutside);
    }
    unbindClickEventListeners() {
        if (this.referenceElement) {
            this.referenceElement.removeEventListener('click', this.toggleTooltip);
        }
        document.removeEventListener('click', this.onClickOutside);
    }
    bindHoverEventListeners() {
        if (this.referenceElement) {
            this.referenceElement.addEventListener('mouseenter', this.showTooltip);
            this.referenceElement.addEventListener('mouseleave', this.hideToolTipReferenceElement);
        }
    }
    unbindHoverEventListeners() {
        if (this.referenceElement) {
            this.referenceElement.removeEventListener('mouseenter', this.showTooltip);
            this.referenceElement.removeEventListener('mouseleave', this.hideToolTipReferenceElement);
        }
        if (this.tooltip) {
            this.tooltip.removeEventListener('mouseenter', this.showTooltip);
            this.tooltip.removeEventListener('mouseleave', this.hideToolTipElement);
        }
    }
    unbindAllEventListeners() {
        this.unbindClickEventListeners();
        this.unbindHoverEventListeners();
    }
    // calls this.hideTooltip if target is not a descendant of the tooltip nor the reference element.
    hideIfOutside(target) {
        if (this.isShown) {
            while (target !== null) {
                if (target === this.tooltip || target === this.referenceElement || (this.props.outsideClickCloseFilter && this.props.outsideClickCloseFilter(target))) {
                    return;
                }
                target = target.parentElement;
            }
            this.hideTooltip();
        }
    }
    isCloseButtonShown() {
        const { isCloseButtonVisible, inputIntent } = this.props;
        return !!(isCloseButtonVisible && ['initial', 'pointer', 'touch', 'keyboard'].indexOf(this.geInputIntent(inputIntent)) > -1);
    }
    BEM(state) {
        const classArray = ['tooltip', `tooltip--trigger-${this.geInputIntent(this.props.inputIntent)}`];
        if (state) {
            classArray.push(`is-${state}`);
        }
        if (this.isShown) {
            classArray.push('is-shown');
        }
        if (this.state.isReady) {
            classArray.push('is-ready');
        }
        if (this.isSpecColor(this.props.color)) {
            classArray.push('tooltip--' + this.props.color);
        }
        if (this.isCloseButtonShown()) {
            classArray.push('tooltip--has-close-button');
        }
        if (this.props.fixedWidth) {
            classArray.push('tooltip--fixed-width');
        }
        if (this.props.noPadding) {
            classArray.push('tooltip--no-padding');
        }
        if (this.props.size) {
            classArray.push('tooltip--' + this.props.size);
        }
        if (this.props.onHeader) {
            classArray.push('tooltip--on-header');
        }
        if (this.props.className) {
            classArray.push(this.props.className);
        }
        return classArray.join(' ');
    }
    renderTooltip() {
        const { CloseButton, PageAccess } = this.props.components;
        const content = (state) => (React.createElement("div", Object.assign({}, this.state.popperAttributes, { className: this.BEM(state), style: this.state.popperStyle, ref: this.setRef, tabIndex: -1, onKeyDown: this.onKeyDown, role: "dialog", "aria-modal": true, "aria-labelledby": this.props['aria-labelledby'] }),
            React.createElement("div", { className: "tooltip__arrow", "data-popper-arrow": true, style: this.state.popperArrowStyle }),
            React.createElement("div", { className: "tooltip__content", style: !this.isSpecColor(this.props.color) ? { borderColor: this.props.color } : undefined },
                this.isCloseButtonShown() && (React.createElement(CloseButton, { className: "tooltip__close", onClick: this.handleCloseClick })),
                this.props.children)));
        if (!this.state.isMounted) {
            return content();
        }
        return (React.createElement(PageAccess, { isInert: this.props.inputIntent !== 'mouse' && this.isShown },
            React.createElement(Transition, { in: this.isShown, timeout: { enter: 0, exit: 100 }, mountOnEnter: true, unmountOnExit: true, onEntered: this.onEntered, onExited: this.onExited }, content)));
    }
    render() {
        return this.state.isMounted ? ReactDOM.createPortal(this.renderTooltip(), document.body) : this.renderTooltip();
    }
}
TooltipInner.defaultProps = {
    fixedWidth: true,
    noPadding: false,
    placement: 'top',
    isCloseButtonVisible: true,
};
